import React, { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import {
  totalPeriodPercent, totalComponentPercent, formatGradeWithScale,
} from "@/lib/grading";

/**
 * Renders a sample student report card using the current Setup state so the
 * owner can sanity-check their configuration.  Uses deterministic mock scores.
 */
export default function PreviewReportCardDialog({ open, onClose, setup, categoryLabel }) {
  const compute = useMemo(() => {
    if (!setup) return null;
    // Generate deterministic "mock" component averages so the preview is stable.
    // Use a simple seeded score per category index — 85 for first, 78 for second, etc.
    const mockSeed = [85, 78, 92, 71, 88, 80];
    const categoryGrades = setup.categories.map((cat, ci) => {
      const baseline = mockSeed[ci % mockSeed.length];
      if (!cat.components.length) return { id: cat.id, name: cat.category_name || "—", grade: null, perPeriod: [] };
      // Per-period grade (calculated mode): we treat the same component average across all periods (mock).
      const perPeriod = setup.periods.map((p, pi) => {
        // Variation per period: +/- 3 per index
        const variation = [-3, 0, 3, -2][pi % 4];
        let weighted = 0;
        for (const c of cat.components) {
          const avg = Math.max(0, Math.min(100, baseline + variation + (c.component_percentage > 50 ? 1 : 0)));
          weighted += avg * (Number(c.component_percentage) || 0) / 100;
        }
        return { period: p, grade: Math.round(weighted * 100) / 100 };
      });
      const avgAcross = perPeriod.length
        ? perPeriod.reduce((a, x) => a + (x.grade || 0), 0) / perPeriod.length
        : null;
      return { id: cat.id, group_id: cat.group_id || null, name: cat.category_name || "—", grade: avgAcross != null ? Math.round(avgAcross * 100) / 100 : null, perPeriod };
    });

    // Final grade per period = average of category grades for that period
    const periodFinals = setup.periods.map((p, pi) => {
      const grades = categoryGrades.map((cg) => cg.perPeriod[pi]?.grade).filter((g) => g != null);
      const avg = grades.length ? grades.reduce((a, g) => a + g, 0) / grades.length : null;
      return { period: p, grade: avg != null ? Math.round(avg * 100) / 100 : null };
    });

    // Final overall (calculated mode)
    let weightedFinal = 0;
    let any = false;
    for (const pf of periodFinals) {
      if (pf.grade != null) {
        any = true;
        weightedFinal += pf.grade * (Number(pf.period.period_percentage) || 0) / 100;
      }
    }
    const finalGrade = any ? Math.round(weightedFinal * 100) / 100 : null;

    return { categoryGrades, periodFinals, finalGrade };
  }, [setup]);

  if (!open) return null;

  const periodPctOk = setup.settings.grading_mode !== "calculated"
    || setup.periods.length === 0
    || totalPeriodPercent(setup.periods) === 100;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="gs-preview-dialog">
      <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-brand-purple/10 sticky top-0 bg-white z-10">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-brand-purple font-heading font-bold">Preview</p>
            <h2 className="font-heading text-2xl font-bold text-foreground">Sample Report Card</h2>
            <p className="text-sm text-brand-inkMuted mt-0.5">Uses mock scores so you can sanity-check the configuration. No data is saved or shown to students.</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="gs-preview-close"><X className="w-5 h-5" /></Button>
        </div>

        <div className="p-5 space-y-5">
          {/* Student header */}
          <div className="rounded-2xl bg-gradient-to-br from-brand-purple to-brand-purpleHover text-white px-5 py-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider opacity-90 font-heading">Student</p>
              <p className="text-xl font-heading font-bold">Juan Dela Cruz</p>
              <p className="text-sm opacity-90">Sample student · {setup.settings.grading_mode === "calculated" ? "Calculated" : "Direct Assessment"} mode</p>
            </div>
            {compute?.finalGrade != null && (
              <div className="text-right">
                <p className="text-xs uppercase tracking-wider opacity-90 font-heading">Final Grade</p>
                <p className="text-3xl font-heading font-bold tabular-nums">{formatGradeWithScale(compute.finalGrade, setup.settings.grading_scale_type, setup.scale_ranges)}</p>
              </div>
            )}
          </div>

          {/* Warnings */}
          {!periodPctOk && (
            <div className="text-sm bg-[#FDECEA] text-[#C62828] border border-[#C62828]/30 rounded-xl px-3 py-2">
              ⚠ Period percentages do not sum to 100%. Calculation may be off.
            </div>
          )}

          {/* Grades table */}
          {!setup.categories.length || !setup.periods.length ? (
            <p className="text-center text-brand-inkMuted py-8">Add at least one {categoryLabel?.toLowerCase().slice(0, -1) || "subject"} and one grading period to preview.</p>
          ) : (
            <div className="overflow-x-auto border border-brand-purple/15 rounded-2xl" data-testid="gs-preview-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-brand-purpleLight/40 text-foreground">
                    <th className="text-left px-3 py-2 font-heading">{categoryLabel || "Subject"}</th>
                    {setup.periods.map((p) => (
                      <th key={p.id} className="px-3 py-2 font-heading text-center">{p.period_name} <span className="text-xs text-brand-inkMuted">({p.period_percentage}%)</span></th>
                    ))}
                    <th className="px-3 py-2 font-heading text-center">Final</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Group category rows by their `group_id` when grouping is
                    // enabled. Renders a spanning header row before each
                    // group's categories. Ungrouped categories fall into a
                    // synthetic "Others" bucket at the end.
                    const groupingOn = !!setup.category_settings?.grouping_enabled && (setup.groups || []).length > 0;
                    if (!groupingOn) {
                      return compute.categoryGrades.map((cg) => (
                        <tr key={cg.id} className="border-t border-brand-purple/10">
                          <td className="px-3 py-2 font-heading">{cg.name}</td>
                          {cg.perPeriod.map((pp, i) => (
                            <td key={i} className="px-3 py-2 text-center tabular-nums">{formatGradeWithScale(pp.grade, setup.settings.grading_scale_type, setup.scale_ranges)}</td>
                          ))}
                          <td className="px-3 py-2 text-center tabular-nums font-heading font-semibold">{formatGradeWithScale(cg.grade, setup.settings.grading_scale_type, setup.scale_ranges)}</td>
                        </tr>
                      ));
                    }
                    const groups = [...(setup.groups || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
                    const buckets = groups.map((g) => ({ group: g, rows: [] }));
                    const orphan = { group: { id: "__orphan__", group_name: "Others" }, rows: [] };
                    for (const cg of compute.categoryGrades) {
                      const b = buckets.find((x) => x.group.id === cg.group_id);
                      (b || orphan).rows.push(cg);
                    }
                    const all = [...buckets, ...(orphan.rows.length ? [orphan] : [])].filter((b) => b.rows.length);
                    const colSpan = 2 + (setup.periods?.length || 0);
                    const out = [];
                    for (const b of all) {
                      out.push(
                        <tr key={`grp-${b.group.id}`} className="bg-brand-purple/10">
                          <td colSpan={colSpan} className="px-3 py-2 font-heading font-bold text-brand-purple text-xs uppercase tracking-wider">{b.group.group_name || "Group"}</td>
                        </tr>
                      );
                      for (const cg of b.rows) {
                        out.push(
                          <tr key={cg.id} className="border-t border-brand-purple/10">
                            <td className="px-3 py-2 font-heading pl-6">{cg.name}</td>
                            {cg.perPeriod.map((pp, i) => (
                              <td key={i} className="px-3 py-2 text-center tabular-nums">{formatGradeWithScale(pp.grade, setup.settings.grading_scale_type, setup.scale_ranges)}</td>
                            ))}
                            <td className="px-3 py-2 text-center tabular-nums font-heading font-semibold">{formatGradeWithScale(cg.grade, setup.settings.grading_scale_type, setup.scale_ranges)}</td>
                          </tr>
                        );
                      }
                    }
                    return out;
                  })()}
                  <tr className="border-t-2 border-brand-purple/30 bg-brand-purpleLight/20">
                    <td className="px-3 py-2 font-heading font-bold">Period Average</td>
                    {compute.periodFinals.map((pf, i) => (
                      <td key={i} className="px-3 py-2 text-center tabular-nums font-heading font-bold">{formatGradeWithScale(pf.grade, setup.settings.grading_scale_type, setup.scale_ranges)}</td>
                    ))}
                    <td className="px-3 py-2 text-center tabular-nums font-heading font-bold text-brand-purple">{formatGradeWithScale(compute.finalGrade, setup.settings.grading_scale_type, setup.scale_ranges)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Scale legend */}
          {setup.settings.grading_scale_type !== "percentage" && setup.scale_ranges.length > 0 && (
            <div>
              <p className="font-heading text-sm font-semibold text-foreground mb-2">Scale Legend</p>
              <div className="flex flex-wrap gap-2">
                {setup.scale_ranges.map((r, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 rounded-full border border-brand-purple/20 bg-brand-purpleLight/30">
                    <strong className="font-heading">{r.code_or_value}</strong>
                    {r.description ? ` · ${r.description}` : ""}
                    {r.min_percentage != null && r.max_percentage != null ? ` (${r.min_percentage}–${r.max_percentage}%)` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-brand-purple/10 flex justify-end sticky bottom-0 bg-white">
          <Button onClick={onClose} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="gs-preview-close-btn">Close Preview</Button>
        </div>
      </div>
    </div>
  );
}
